/**
 * Move generator strategy pattern for MoveChallenge missions.
 * Each strategy returns a board state (FEN) and the expected move (SAN).
 *
 * Strategies:
 * - tactics: Capture an undefended piece in one move.
 * - check: Deliver check to the opponent's king in one move.
 * - defense: Defend your king from check in one move (escape check).
 *
 * Add new strategies as needed for new mission focuses.
 */

import { generateRandomSan } from '../../utils/chessRandomUtils';
import { Chess } from 'chess.js';
import { getCanonicalMissionMoveSan } from './missionMoveSan';

export interface MoveGeneratorResult {
  position: string;      // FEN
  targetMove: string;    // SAN
  isFallback?: boolean;  // Optional: true if this is a fallback, for diagnostics
  // Add more fields as needed (e.g., reason, difficulty, etc.)
}

export type MoveGenerator = (fallback: MoveGeneratorResult) => MoveGeneratorResult;

/**
 * Registry of move generator strategies by focus key.
 */
export const moveGeneratorStrategies: Record<string, MoveGenerator> = {
  tactics: generateTacticPosition,
  check: generateCheckPosition,
  defense: generateDefensePosition,
  // Add more strategies here as needed
};

export const EMPTY_BOARD_FALLBACK: MoveGeneratorResult = {
  position: 'rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1',
  targetMove: 'e4',
  isFallback: true,
};

export const KING_IN_CHECK_FALLBACK: MoveGeneratorResult = {
  position: 'rnb1kbnr/ppppqppp/8/8/8/3P4/PPP2PPP/RNBQKBNR w KQkq - 0 1',
  targetMove: 'Kd2',
  isFallback: true,
};

/**
 * For missions with focus 'tactics', generate a tactical position and expected move.
 * The player must capture an undefended piece in one move.
 *
 * Attempts to find a random position with such a move, or adds an undefended piece if needed.
 */
export function generateTacticPosition(fallback: MoveGeneratorResult): MoveGeneratorResult {
  // Try up to 10 times to find a valid tactical position
  for (let attempt = 0; attempt < 10; attempt++) {
    // Generate a random position
    const { fen } = generateRandomSan(new Set());
    const chess = new Chess(fen);
    const color = chess.turn();
    const opponent = color === 'w' ? 'b' : 'w';
    // Find all legal capture moves
    const moves = chess.moves({ verbose: true }).filter((m: any) => m.flags.includes('c') || m.flags.includes('e'));
    // Shuffle the moves to randomize the order
    moves.sort(() => Math.random() - 0.5);
    for (const move of moves) {
      // Use chess.js's isAttacked for defense checking
      if (!chess.isAttacked(move.to, opponent)) {
        // This is a capture of an undefended piece
        return { position: fen, targetMove: getCanonicalMissionMoveSan(fen, move.san) };
      }
    }
    // If not found, try again
  }
  // Fallback: return a default empty board
  return fallback;
}

/**
 * Generates a position and a legal move that puts the opponent's king in check.
 * Used for missions with focus: 'check'.
 *
 * Attempts to find a random position with such a move, or falls back to a default if not found.
 */
export function generateCheckPosition(fallback: MoveGeneratorResult): MoveGeneratorResult {
  // Try up to 10 times to find a valid check position
  for (let attempt = 0; attempt < 10; attempt++) {
    // Generate a random position
    const { fen } = generateRandomSan(new Set());
    const chess = new Chess(fen);
    
    const moves = chess.moves({ verbose: true });
    // Shuffle the moves to randomize the order
    moves.sort(() => Math.random() - 0.5);
    for (const move of moves) {
      chess.move(move.san);
      if (chess.inCheck()) {
        // This move delivers check
        const result = { position: fen, targetMove: getCanonicalMissionMoveSan(fen, move.san) };
        chess.undo();
        return result;
      }
      chess.undo();
    }
    // If not found, try again
  }
  // Fallback: return a default position with a known check move
  return fallback;
}

/**
 * Generates a position and a legal move that gets the player's king out of check in one move.
 * Used for missions with focus: 'defense'.
 *
 * Attempts to find a random position where the side to move is in check and has at least one legal move
 * that escapes check (i.e., after the move, the king is not in check). Returns the FEN and the SAN of such a move.
 * Falls back to the provided fallback if no such position is found.
 */
export function generateDefensePosition(fallback: MoveGeneratorResult): MoveGeneratorResult {
  for (let attempt = 0; attempt < 10; attempt++) {
    
    const badState = function(fen: string) {
      const chess = new Chess(fen);
      return !chess.isCheck();
    }
    
    let fen: string;
    let count = 0;
    do {
    // Generate a random position
    const result = generateRandomSan(new Set(), undefined, undefined, true);
    fen = result.fen;
    } while (badState(fen) && count++ < 1000);
    
    const chess = new Chess(fen);
    // Only consider positions where the side to move is in check
    if (!chess.inCheck()) continue;
    // Find all legal moves
    const moves = chess.moves({ verbose: true });
    // Shuffle the moves to randomize the order
    moves.sort(() => Math.random() - 0.5);
    for (const move of moves) {
      chess.move(move.san);
      // If after this move, the king is no longer in check, it's a valid defensive move
      if (!chess.inCheck()) {
        const result = { position: fen, targetMove: getCanonicalMissionMoveSan(fen, move.san) };
        chess.undo();
        return result;
      }
      chess.undo();
    }
    // If not found, try again
  }
  // Fallback: return the provided fallback
  return fallback;
} 
